import dayjs, { Dayjs } from 'dayjs';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, FormikErrors, useField } from 'formik';
import { FormattedMessage } from 'react-intl';
import { InputAdornment } from '@mui/material';
import { PickerChangeHandlerContext, TimeValidationError } from '@mui/x-date-pickers';
import { ReactElement, ReactNode, useEffect, useState } from 'react';

import { PoolButton } from '../buttons';
import { PoolTimePicker, PoolTimePickerNewProps } from './pool-time-picker';

export interface FormikPoolTimePickerProps extends Omit<PoolTimePickerNewProps, 'name' | 'onChange'> {
    name: string; // redefined as required
    onChange?: (
        value: Dayjs | null,
        helper: FieldHelperProps<Dayjs | null>,
        field: FieldInputProps<Dayjs | null>,
        meta: FieldMetaProps<Dayjs | null>
    ) => void;
}

export const FormikPoolTimePicker = (props: FormikPoolTimePickerProps): ReactElement => {
    const { onChange, ...pickerProps } = props;
    const [field, meta, helper] = useField<Dayjs | null>(props.name);
    const hasFormikError = meta.touched && !!meta.error;
    const [currentValue, setCurrentValue] = useState<Dayjs | null>(field.value ? dayjs(field.value).hour(12) : null);

    useEffect(() => {
        const value = field.value ? dayjs(field.value) : null;
        setCurrentValue(value);
        onChange?.(value, helper, field, meta);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value]);

    const handleChange = (value: Dayjs | null, context: PickerChangeHandlerContext<TimeValidationError>): void => {
        setCurrentValue(value);
    };

    const setFieldValue = (value: Dayjs | null): Promise<void | FormikErrors<Dayjs | null>> =>
        helper.setValue(value ?? null);

    const getHelperText = (): ReactNode => {
        if (hasFormikError) {
            return <FormattedMessage id={meta.error} />;
        }
        return undefined;
    };

    return (
        <PoolTimePicker
            {...pickerProps}
            slotProps={{
                textField: {
                    onBlur: () => setFieldValue(currentValue),
                },
                inputAdornment: (
                    <InputAdornment position="start">
                        <PoolButton>Submit</PoolButton>
                    </InputAdornment>
                ),
            }}
            helperText={getHelperText()}
            name={field.name}
            error={hasFormikError}
            value={currentValue}
            onChange={handleChange}
            onAccept={value => setFieldValue(value)}
        />
    );
};
